import { Box, Grid, Menu, MenuItem } from "@mui/material";
import { OLButton } from "OLButton";
import { OLPaper } from "OLPaper";
import { getBackendErrors, getErrorMessage } from "api/errors";
import { useTiers } from "api/tiers";
import { useAppSelector } from "app/hooks";
import { OLClearIcon } from "components/CustomIcons/OLClearIcon";
import { DateTimeInput } from "components/FormControls/DateTimeInput";
import { DragDropInput, File } from "components/FormControls/DragDropInput";
import { FormButtons } from "components/FormControls/FormButtons";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { TextInput } from "components/FormControls/TextInput";
import { Toggle } from "components/FormControls/Toggle";
import { Breadcrumbs, TopHeader } from "components/Header/TopHeader/TopHeader";
import { ReactComponent as PlusIcon } from "components/Icons/plus.svg";
import { LeaveSiteGuard } from "components/Tooltips/LeaveSiteGuard";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { FieldArray, Form, Formik } from "formik";
import { useContextMenu } from "hooks/contextMenu";
import { useMessage } from "hooks/intl";
import React, { useState } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useNavigate } from "react-router-dom";
import {
  selectAvaiableTranslations,
  selectDefaultLanguageData,
} from "reduxState/translations/translationsSlice";
import { TierPayload } from "types/tierSets";
import {
  backendErrors,
  parseTranslationsArrayError,
} from "utilities/backendErrors";
import { focusError } from "utilities/focusError";
import { links } from "utilities/links";
import { useStyles } from "./TierForm.styles";

export function AddTier() {
  const classes = useStyles();
  const { notify } = useNotifications();
  const { addTier, setPhoto } = useTiers();
  const navigate = useNavigate();
  const { message } = useMessage();
  const availableLanguages = useAppSelector(selectAvaiableTranslations);
  const [files, setFiles] = useState<File[]>([]);
  const defaultLang = useAppSelector(selectDefaultLanguageData);
  const initialValues: TierPayload = {
    active: false,
    conditionValue: "",
    photo: undefined,
    rewards: [],
    translations: [
      defaultLang && {
        description: "",
        code: defaultLang.code,
        languageName: defaultLang.name,
        name: "",
      },
    ],
  };
  const contextMenu = useContextMenu();
  const transformTranslations = (translations: any[]) =>
    translations.reduce(
      (acc: any, cur: { code: string; description: string; name: string }) => ({
        ...acc,
        [cur.code]: {
          description: cur.description,
          name: cur.name,
        },
      }),
      {},
    );

  const breadcrumbs: Breadcrumbs = {
    parent: {
      label: message({ id: "listOfTiers" }),
      path: links.tiersList,
    },
  };

  return (
    <>
      <TopHeader
        title={message({ id: "addTier" })}
        clearLink={links.tiersList}
        breadcrumbs={breadcrumbs}
      />
      {initialValues && defaultLang ? (
        <Formik
          initialStatus={{}}
          initialValues={initialValues}
          onSubmit={async (values, { setSubmitting, setStatus }) => {
            setSubmitting(true);
            setStatus({});
            const { photo, ...formProps } = { ...values };
            try {
              const response = await addTier({
                ...formProps,
                translations: transformTranslations(values.translations),
              });
              if (response.data.levelId) {
                if (values.photo) {
                  await setPhoto(response.data.levelId, values.photo);
                }
                navigate(links.tiersList);
                notify(message({ id: "tierAdded" }));
              }
            } catch (exception) {
              setSubmitting(false);
              notify(getErrorMessage(exception));
              const errors = backendErrors(getBackendErrors(exception));
              const parsedErrors = {
                ...errors,
                translations: parseTranslationsArrayError(
                  errors.translations,
                  values.translations,
                ),
              };
              setStatus(parsedErrors);
              focusError();
            }
          }}
        >
          {({
            handleSubmit,
            isSubmitting,
            status,
            setFieldValue,
            values,
            dirty,
            initialValues,
          }) => {
            return (
              <Form onSubmit={handleSubmit}>
                {status?.[""] && (
                  <span className={classes.error}>{status[""]}</span>
                )}
                <OLPaper className={classes.OLPaper}>
                  <Grid container className={classes.displayColumn}>
                    <FieldArray
                      name="translations"
                      render={(arrayHelpers) => (
                        <>
                          {typeof values?.translations?.length !==
                            "undefined" &&
                            values?.translations?.length > 0 &&
                            values?.translations.map((item, index) => (
                              <section
                                className={classes.conditionSection}
                                key={index}
                              >
                                <div className={classes.conditionWrapper}>
                                  <div className={classes.TooltipWrapper}>
                                    <TextInput
                                      label={
                                        index === 0 ? (
                                          <RequiredFieldLabel
                                            text={`${message({
                                              id: "name",
                                            })} (${item?.languageName})`}
                                          />
                                        ) : (
                                          `${message({
                                            id: "name",
                                          })} (${item?.languageName})`
                                        )
                                      }
                                      name={`translations[${index}].name`}
                                      data-test-id={`name-${item.code}`}
                                    />
                                    <TextInput
                                      label={`${message({
                                        id: "description",
                                      })} (${item?.languageName})`}
                                      name={`translations[${index}].description`}
                                      data-test-id={`desc-${item.code}`}
                                    />
                                  </div>
                                  {index > 0 && (
                                    <OLClearIcon
                                      className={classes.clearLink}
                                      onClick={() => arrayHelpers.remove(index)}
                                    />
                                  )}
                                </div>
                              </section>
                            ))}
                          <div>
                            <OLButton
                              className={classes.addTranslation}
                              data-test-id="add-translation"
                              color="secondary"
                              onClick={
                                availableLanguages.filter(
                                  (lang: { code?: string }) =>
                                    !values.translations.find(
                                      (openLang) => openLang.code === lang.code,
                                    ),
                                ).length
                                  ? contextMenu.handleMenuOpen
                                  : undefined
                              }
                              startIcon={<PlusIcon />}
                            >
                              {message({
                                id: "addTranslation",
                              })}
                            </OLButton>
                          </div>
                          <Menu
                            anchorEl={contextMenu.anchor}
                            anchorOrigin={{
                              horizontal: "right",
                              vertical: "top",
                            }}
                            onClose={contextMenu.handleMenuClose}
                            open={contextMenu.isMenuOpen}
                            transformOrigin={{
                              horizontal: "right",
                              vertical: "top",
                            }}
                          >
                            {availableLanguages
                              ?.filter(
                                (lang: { code?: string }) =>
                                  !values.translations.find(
                                    (openLang) => openLang.code === lang.code,
                                  ),
                              )
                              .map(
                                (language: {
                                  code?: string | number;
                                  name: React.ReactNode;
                                }) => (
                                  <MenuItem
                                    key={language.code}
                                    onClick={() => {
                                      contextMenu.handleMenuClose();
                                      arrayHelpers.push({
                                        description: "",
                                        code: language.code,
                                        languageName: language.name,
                                        name: "",
                                      });
                                    }}
                                    data-test-id={`translation-${language.code}`}
                                  >
                                    {language.name}
                                  </MenuItem>
                                ),
                              )}
                          </Menu>
                        </>
                      )}
                    />
                    <Toggle
                      label={message({ id: "active" })}
                      name="active"
                      data-test-id="active"
                    />
                    <TextInput
                      label={
                        <RequiredFieldLabel
                          text={message({ id: "conditionValue" })}
                        />
                      }
                      name="conditionValue"
                      type="number"
                      data-test-id="condition-value"
                    />
                    <FieldArray
                      name="rewards"
                      render={(arrayHelpers) => (
                        <>
                          {typeof values?.rewards?.length !== "undefined" &&
                            values?.rewards?.length > 0 &&
                            values?.rewards.map((_, index) => (
                              <section
                                className={classes.conditionSection}
                                key={index}
                              >
                                <div className={classes.conditionWrapper}>
                                  <div className={classes.TooltipWrapper}>
                                    <TextInput
                                      label={
                                        <RequiredFieldLabel
                                          text={message({
                                            id: "rewardName",
                                          })}
                                        />
                                      }
                                      name={`rewards[${index}].name`}
                                      data-test-id={`special-reward-${index}-name`}
                                    />
                                    <TextInput
                                      label={
                                        <RequiredFieldLabel
                                          text={message({
                                            id: "rewardCode",
                                          })}
                                        />
                                      }
                                      name={`rewards[${index}].code`}
                                      data-test-id={`special-reward-${index}-code`}
                                    />
                                    <DateTimeInput
                                      format="yyyy-MM-dd HH:mmXXX"
                                      label={
                                        <RequiredFieldLabel
                                          text={message({
                                            id: "startAt",
                                          })}
                                        />
                                      }
                                      name={`rewards[${index}].startAt`}
                                      data-test-id={`special-reward-${index}-start-at`}
                                    />
                                    <DateTimeInput
                                      format="yyyy-MM-dd HH:mmXXX"
                                      label={
                                        <RequiredFieldLabel
                                          text={message({
                                            id: "endAt",
                                          })}
                                        />
                                      }
                                      name={`rewards[${index}].endAt`}
                                      data-test-id={`special-reward-${index}-end-at`}
                                    />
                                    <TextInput
                                      label={
                                        <RequiredFieldLabel
                                          text={message({
                                            id: "value",
                                          })}
                                        />
                                      }
                                      name={`rewards[${index}].value`}
                                      type="number"
                                      data-test-id={`special-reward-${index}-value`}
                                    />
                                    <Toggle
                                      className={classes.toggle}
                                      label={message({
                                        id: "active",
                                      })}
                                      name={`rewards[${index}].active`}
                                      data-test-id={`special-reward-${index}-active`}
                                    />
                                  </div>
                                  <OLClearIcon
                                    className={classes.clearLink}
                                    onClick={() => arrayHelpers.remove(index)}
                                    data-test-id={`special-reward-${index}-remove`}
                                  />
                                </div>
                              </section>
                            ))}
                          <OLButton
                            className={`${classes.button} ${classes.alignSelf}`}
                            color="primary"
                            variant="contained"
                            onClick={() =>
                              arrayHelpers.push({
                                code: "",
                                endAt: null,
                                name: "",
                                startAt: null,
                              })
                            }
                            data-test-id={`add-special-reward`}
                          >
                            {message({
                              id: "addSpecialReward",
                            })}
                          </OLButton>
                        </>
                      )}
                    />
                  </Grid>
                  <Grid alignItems="flex-start" container direction="column">
                    {status?.file && (
                      <span
                        className={`${classes.error} ${classes.dragDropWrapper} error-anchor`}
                      >
                        {status.file}
                      </span>
                    )}
                    <DragDropInput
                      className={classes.dragDropWrapper}
                      description={
                        <span className={classes.dragDropText}>
                          {message({ id: "acceptedFormats" })}
                        </span>
                      }
                      header={message({ id: "tierPhoto" })}
                      inputClassName={classes.fullWidth}
                      singleFile
                      square
                      onChange={(acceptedFiles, rejectedFiles) => {
                        const formData = new FormData();
                        formData.append("image", acceptedFiles[0]);
                        acceptedFiles.length > 0
                          ? setFiles([
                              {
                                ...acceptedFiles[0],
                                preview: URL.createObjectURL(acceptedFiles[0]),
                              },
                            ])
                          : setFiles([
                              {
                                ...rejectedFiles[0].file,
                                preview: URL.createObjectURL(
                                  rejectedFiles[0].file,
                                ),
                              },
                            ]);
                        setFieldValue(
                          "photo",
                          acceptedFiles.length > 0
                            ? acceptedFiles[0]
                            : rejectedFiles[0].file,
                        );
                      }}
                      onDelete={() => {
                        setFieldValue("photo", undefined);
                        setFiles([]);
                      }}
                      value={files}
                      data-test-id={`tier-photo`}
                    />
                  </Grid>
                </OLPaper>
                <Box mt={12} />
                <FormButtons
                  disableSubmit={isSubmitting}
                  onCancelClick={() => navigate(links.tiersList)}
                  onSubmitClick={() => handleSubmit()}
                  submitMessage="addTier"
                />
                {!isSubmitting && (
                  <LeaveSiteGuard
                    warnBeforeRefresh
                    isDirty={dirty}
                    initialValues={initialValues}
                    currentValues={values}
                  />
                )}
              </Form>
            );
          }}
        </Formik>
      ) : (
        <>
          <Skeleton height={300} width={"100%"} />
          <Box mt={12} />
          <FormButtons
            disableSubmit
            onCancelClick={() => {}}
            onSubmitClick={() => {}}
            submitMessage="addTier"
          />
        </>
      )}
    </>
  );
}
